import { componentProvider } from 'js-component-framework/es';

/**
 * Class to set up an interactive Menu element.
 *
 * https://www.w3.org/WAI/ARIA/apg/example-index/disclosure/disclosure-navigation.html
 */
export default class Menu {
  /**
   * Tracks the active submenu Disclosure.
   * @private
   *
   * @type {object}
   */
  #activeDisclosure = undefined;

  /**
   * Create a Menu.
   * @constructor
   *
   * @param {HTMLUListElement} options.element The menu list element.
   */
  constructor({ element }) {
    /**
     * the component element.
     *
     * @type {HTMLULElement}
     */
    this.element = element;

    /**
     * Submenu Disclosures.
     *
     * @type {array}
     */
    this.disclosures = [];

    /**
     * The clas to add to disclosure parent items when the submenu is expanded.
     *
     * @type {string}
     */
    this.expandedItemClass = 'menu__item--submenu-is-expanded';

    /**
     * The current Menu item, if any.
     *
     * @type {HTMLAnchorElement}
     */
    this.ariaCurrentPage = null;

    // Make sure the component element is a list.
    if (['UL', 'OL'].includes(element.nodeName)) {
      this.init();
    }
  }

  /**
   * Set the active Disclosure ID, which conditionally sets the active Disclosure.
   *
   * @param {string} disclosureId The Disclosure ID.
   */
  set activeDisclosureId(disclosureId) {
    // Find the Disclosure by ID.
    const disclosure = this.disclosures.find((obj) => obj.id === disclosureId);

    // Get the Disclosure's current state.
    const isActiveId = (
      undefined !== this.activeDisclosureId
      && this.activeDisclosureId === disclosureId
    );

    // Toggle the Disclosure's state.
    this.setDisclosureState(disclosure, !isActiveId);

    // Deactivate the currently-active disclosure, if any.
    this.setDisclosureState(this.activeDisclosure, false);

    // If `disclosureId` was the active ID, then it is no longer active.
    this.#activeDisclosure = isActiveId ? undefined : disclosure;

    if (this.activeDisclosureId) {
      document.body.addEventListener('keydown', this.bodyHandleKeydown);
    } else {
      document.body.removeEventListener('keydown', this.bodyHandleKeydown);
    }
  }

  /**
   * Returns the active Disclosure ID.
   *
   * @return {string|undefined}
   */
  get activeDisclosureId() {
    return this.activeDisclosure?.id;
  }

  /**
   * Returns the active Disclosure.
   *
   * @return {object|undefined}
   */
  get activeDisclosure() {
    return this.#activeDisclosure;
  }

  /**
   * Initialize and save a submenu Disclosure.
   *
   * @param  {array}         disclosures The array of previous disclosures.
   * @param  {HTMLLIElement} menuChild   The menu item.
   * @return {array} A collection of submenu Disclosures.
   */
  initSubmenuDisclosure = (disclosures, menuChild) => {
    let controller = menuChild.querySelector(':scope > [aria-controls]');

    if (controller === null) {
      const [firstChild, ...theRest] = Array.from(menuChild.children);

      // Try to use the first child of the menu item.
      controller = firstChild;

      if (controller == null || !controller.matches('a,button')) {
        // The first child isn't a link or button, so find the first instance of either.
        [controller] = Array.from(theRest).filter((child) => child.matches('a,button'));
      }
    }

    if (controller == null) {
      return disclosures;
    }

    const target = controller.parentElement.querySelector('ul');
    if (target == null) {
      return disclosures;
    }

    controller.setAttribute('aria-expanded', false);
    target.setAttribute('aria-hidden', true);

    // Do this last to be sure the target has an ID.
    controller.setAttribute('aria-controls', target.id);

    controller.addEventListener('click', this.controllerHandleClick);
    controller.addEventListener('keydown', this.controllerHandleKeydown);

    const disclosure = {
      id: controller.id,
      controller,
      target,
    };

    return [...disclosures, disclosure];
  };

  /**
   * Collect menu links and recursively instantiate sublist menu items.
   */
  init = () => {
    // Set and collect submenu Disclosures.
    this.disclosures = Array.from(this.element.children)
      .reduce(this.initSubmenuDisclosure, []);

    if (this.disclosures.length > 0) {
      this.element.addEventListener('focusout', this.menuHandleFocusout);
    }

    // Save the current page's link for later reference.
    this.ariaCurrentPage = this.element.querySelector('[aria-current="page"]');
    this.element.addEventListener('keydown', this.menuHandleKeydown);
  };

  /**
   * Update attributes based on expanded state.
   *
   * @param {object} disclosure The Disclosure to update.
   * @param {boolean} expanded  The expected updated Disclosure state.
   */
  setDisclosureState = (disclosure, expanded) => {
    if (undefined !== disclosure) {
      disclosure.controller.setAttribute('aria-expanded', expanded);
      disclosure.target.setAttribute('aria-hidden', !expanded);

      const disclosureParent = disclosure.controller?.parentElement;
      const methodName = expanded ? 'add' : 'remove';

      if (undefined !== disclosureParent) {
        disclosureParent.classList[methodName](this.expandedItemClass);
      }
    }
  };

  /**
   * Activate the submenu Disclosure on controller click.
   *
   * @param {Event} event The Event object.
   */
  controllerHandleClick = (event) => {
    event.preventDefault();

    this.activeDisclosureId = event.target.id;
  };

  /**
   * Toggle Disclosures on controller keydown.
   *
   * @param {Event} event The Event object.
   */
  controllerHandleKeydown = (event) => {
    const { target, key } = event;

    if ([' ', 'Enter'].includes(key)) {
      event.preventDefault();
      event.stopPropagation();

      this.activeDisclosureId = target.id;
    }
  };

  /**
   * Close any active submenu Disclosure when the menu no longer contains the
   * active element.
   *
   * @param {Event} event The Event object.
   */
  menuHandleFocusout = (event) => {
    if (!this.element.contains(event.relatedTarget)) {
      this.activeDisclosureId = undefined;
    }
  };

  /**
   * Add the aria-active attribute to the focused menu item, removing it from
   * any other item in the processs.
   *
   * @param {Event} event The Event object.
   */
  menuHandleKeydown = (event) => {
    const { target, key } = event;

    if (
      [' ', 'Enter'].includes(key)
      && !target.hasAttribute('aria-controls')
      && !target.hasAttribute('aria-current')
    ) {
      this.ariaCurrentPage?.removeAttribute('aria-current');
      target.setAttribute('aria-current', 'page');

      this.ariaCurrentPage = target;
    }
  };

  /**
   * Close any active submenu Disclosure when the Escape key is pressed.
   *
   * @param {Event} event The Event object.
   */
  bodyHandleKeydown = (event) => {
    if (
      event.key === 'Escape'
      && undefined !== this.activeDisclosureId
    ) {
      const { controller, target } = this.activeDisclosure;

      this.activeDisclosureId = undefined;

      if (target.contains(document.activeElement)) {
        controller.focus();
      }
    }
  };
}

componentProvider({ name: 'menu', component: Menu });
